<template>
  <div class="check-list-item" :style="{ gap: `${markerTextSpacing !== undefined ? markerTextSpacing : 20}px` }">
    <!-- Явные шаблоны по типам для простой правки макета -->
    <CheckMarkerSymbol v-if="type === CheckMarker.One"
                       :style-obj="foregroundStyle"
                       :src="markerImagePath"
                       :gradient-style="gradientStyle"
                       :marker-vertical-offset="markerVerticalOffset" />

    <CheckMarkerSymbol v-else-if="type === CheckMarker.Two"
                       :style-obj="foregroundStyle"
                       :src="markerImagePath"
                       :use-svg="isUseCustomMarkerColor"
                       :svg-options="svgOptions"
                       :gradient-style="gradientStyle"
                       :marker-vertical-offset="markerVerticalOffset" />

    <CheckMarkerSymbol v-else-if="type === CheckMarker.Three"
                       :style-obj="foregroundStyle"
                       :src="markerImagePath"
                       :use-svg="isUseCustomMarkerColor"
                       :svg-options="svgOptions"
                       :gradient-style="gradientStyle"
                       :marker-vertical-offset="markerVerticalOffset" />

    <CheckMarkerSymbol v-else-if="type === CheckMarker.Four"
                       :style-obj="foregroundStyle"
                       :src="markerImagePath"
                       :use-svg="isUseCustomMarkerColor"
                       :svg-options="svgOptions"
                       :gradient-style="gradientStyle"
                       :marker-vertical-offset="markerVerticalOffset" />

    <CheckMarkerSymbol v-else-if="type === CheckMarker.Five"
                       :style-obj="foregroundStyle"
                       :src="markerImagePath"
                       :use-svg="isUseCustomMarkerColor"
                       :svg-options="svgOptions"
                       :gradient-style="gradientStyle"
                       :marker-vertical-offset="markerVerticalOffset" />

    <!-- fallback: check21-29 та решта — повна підтримка кольорів через mask/VSvg -->
    <CheckMarkerSymbol v-else
                       :style-obj="foregroundStyle"
                       :src="markerImagePath"
                       :gradient-style="gradientStyle"
                       :marker-vertical-offset="markerVerticalOffset" />

    <div class="list-item-text" v-if="!labelDisabled">
      <div
        v-if="showTitle && title"
        class="list-item-title"
        v-html="title"
      ></div>
      <VBibleInteractive :html="text ?? ''" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {useMarkerTypes, CheckMarker} from '../../composables/markers';
import CheckMarkerSymbol from './symbols/CheckMarkerSymbol.vue';

const props = withDefaults(defineProps<{
  text?: string;
  title?: string;
  showTitle?: boolean;
  index: number;
  markerType: string;
  markerBackgroudColor?: string;
  markerTextColor?: string;
  labelDisabled?: boolean;
  isUseCustomMarkerColor?: boolean;
  markerTextSpacing?: number;
  markerVerticalOffset?: number;
}>(), {
  text: ''
});

// Используем композабл для работы с маркерами
const {getMarkerImagePath, getMarkerConfig} = useMarkerTypes();

// Путь к изображению маркера
const markerImagePath = computed(() => getMarkerImagePath(props.markerType));

// Опции для VSvg
const svgOptions = computed(() => {
  const config = getMarkerConfig(props.markerType);
  const isGradient = !!config?.backgroundGradient;
  const resolvedColor = props.isUseCustomMarkerColor ? props.markerTextColor : config?.textColor;

  return {
    color: isGradient ? 'transparent' : resolvedColor,
    width: '30px',
    height: '30px'
  };
});

// Стили для градиентного фона
const gradientStyle = computed(() => {
  const config = getMarkerConfig(props.markerType);
  const resolvedBg = props.isUseCustomMarkerColor ? props.markerBackgroudColor : config?.backgroundGradient;

  if (resolvedBg) {
    return {
      background: resolvedBg,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    };
  }
  return {};
});

// Цвет галочки через CSS-переменную для SVG (stroke/fill uses var(--marker-foreground))
const foregroundStyle = computed(() => {
  const config = getMarkerConfig(props.markerType);
  const resolvedColor = props.isUseCustomMarkerColor ? props.markerTextColor : config?.textColor;

  return resolvedColor ? ({ '--marker-foreground': resolvedColor } as any) : ({} as any);
});

const type = computed(() => props.markerType);

const base = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  flexShrink: 0,
  minWidth: '40px'
} as const;

const resolvedOutlineColor = computed(() => {
  const custom = (props.markerBackgroudColor || '').trim();
  return custom || '#14B8A6';
});

</script>

<style scoped>
.check-list-item {
  display: flex;
  width: 100%;
}

.check-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.check-image {
  width: 30px;
  height: 30px;
}

.list-item-text {
  flex: 1;
  font-size: 16px;
  line-height: 1.5;
  color: #374151;
}

.list-item-text :deep(b),
.list-item-text :deep(strong) {
  font-weight: 600;
}

.list-item-title {
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  margin-bottom: 4px;
}

/* Адаптивность */
@media (max-width: 768px) {
  .check-marker {
    width: 32px;
    height: 32px;
    min-width: 32px;
  }

  .check-image {
    width: 20px;
    height: 20px;
  }

  .list-item-text {
    font-size: 14px;
  }
}
</style>
